import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Divider, Button, Group, Title, Checkbox, createStyles, Box, Image, ColorInput, Text } from '@mantine/core';
import TenantLogoDropzone from './TenantLogoDropzone';
import { notifications } from '@mantine/notifications';
import { saveAdminTenant, updateAdminTenant } from '../../services/api.service';
import { IconCheck } from '@tabler/icons-react';
import { colors } from '../../lib/colors';

// Custom styles
const useStyles = createStyles((theme) => ({
	imageContainer: {
		border: '1px solid #CCCCCC',
		padding: 3,
		borderRadius: theme.radius.sm,
	},
	fieldError: {
		fontFamily: 'Montserrat, sans-serif',
		color: '#FF8787',
		fontSize: 'calc(0.875rem - 0.125rem)',
		lineHeight: 1.2,
	}
}));

const TenantForm = ({ closeModal, isEdit, tenant }) => {

	// Hooks
	const [isLoading, toggleLoading] = useState(false);
	const [fileURL, setFileURL] = useState('');
	const [isotypeURL, setIsotypeURL] = useState('');
	const [isLogoChanged, toggleIsLogoChanged] = useState(false);
	const [isIsotypeChanged, toggleIsIsotypeChanged] = useState(false);
	const { classes } = useStyles();
	const form = useForm({
		initialValues: {
			name: '',
			referalCode: '',
			primaryColor: '',
			secondaryColor: '',
			tertiaryColor: '',
			active: true,
			logo: null,
			isotype: null,
		},
		validate: {
			name: (value) => value !== '' ? null : 'El nombre del partner es requerido.',
			referalCode: (value) => value !== '' ? null : 'El código de referido es requerido.',
			primaryColor: (value) => value !== '' ? null : 'Debes elegir un color primario.',
			logo: (value) => value ? null : 'Debes agregar un logotipo.',
			isotype: (value) => value ? null : 'Debes agregar un isotipo.',
		},
	});

	// Check if edit
	useEffect(() => {
		if (isEdit) form.setValues({
			name: tenant.name,
			referalCode: tenant.referalCode,
			primaryColor: tenant.primaryColor,
			secondaryColor: tenant.secondaryColor,
			tertiaryColor: tenant.tertiaryColor,
			active: tenant.active,
			logo: tenant.logo,
			isotype: tenant.isotype,
		});
		// eslint-disable-next-line
	}, []);

	// Submit form
	const onSubmit = (values) => {

		// Set loader
		toggleLoading(true);

		// Conditions
		if (!isEdit) {

			// Call API
			saveAdminTenant(values, function () {

				// Show success
				notifications.show({
					title: 'Cliente corporativo creado correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		} else {

			// Call API
			updateAdminTenant(tenant.id, values, function () {

				// Show success
				notifications.show({
					title: 'Cliente corporativo modificado correctamente',
					color: 'green',
					icon: <IconCheck />
				});

				// Close after 1 second
				setTimeout(() => {

					// Close modal
					closeModal();

				}, 1000);

			}, function (error) {

				// Remove preloader
				toggleLoading(false);

				// Show error
				notifications.show({
					title: 'Error',
					message: error?.message?.[0] || 'Error inesperado.',
					color: 'red'
				});

			});

		}

	}

	// Process accepted file
	const onDropLogo = (file) => {
		form.setFieldValue('logo', file[0]);
		setFileURL(URL.createObjectURL(file[0]));
		toggleIsLogoChanged(true);
	}

	// Process accepted file
	const onDropIsotype = (file) => {
		form.setFieldValue('isotype', file[0]);
		setIsotypeURL(URL.createObjectURL(file[0]));
		toggleIsIsotypeChanged(true);
	}

	// Reject file and show error
	const onReject = (file) => {
		notifications.show({
			title: 'Error',
			message: 'Este tipo de archivo no está soportado.',
			color: 'red'
		});
	}

	// On primary color change
	useEffect(() => {

		// If color selected
		if (form.values.primaryColor) {

			// Assign secondary and tertiary
			form.setFieldValue('secondaryColor', colors[form.values.primaryColor].secondary);
			form.setFieldValue('tertiaryColor', colors[form.values.primaryColor].tertiary);

		}

		// eslint-disable-next-line
	}, [form.values.primaryColor]);

	return (
		<>
			{/* Form */}
			<form onSubmit={form.onSubmit(onSubmit)}>
				{/* Title */}
				<Title
					order={5}
					mb="md"
				>
					{isEdit ? 'Modificar' : ''} Información de Cliente Corporativo
				</Title>
				{/* Input Group */}
				<Group grow>
					{/* Name */}
					<TextInput
						withAsterisk
						label="Nombre"
						placeholder="Nombre del cliente"
						value={form.values.name}
						onChange={(event) => form.setFieldValue('name', event.currentTarget.value)}
						{...form.getInputProps('name')}
					/>
					{/* Referal Code */}
					<TextInput
						withAsterisk
						label="Código de referido"
						placeholder="Escribe un código de referido"
						value={form.values.referalCode}
						onChange={(event) => form.setFieldValue('referalCode', event.currentTarget.value)}
						{...form.getInputProps('referalCode')}
					/>
					{/* Color */}
					<ColorInput
						withAsterisk
						placeholder="Elige un color"
						label="Color primario"
						disallowInput
						withPicker={false}
						withEyeDropper={false}
						value={form.values.primaryColor}
						onChange={(event) => form.setFieldValue('primaryColor', event.currentTarget.value)}
						{...form.getInputProps('primaryColor')}
						swatches={[
							'#EF5820',
							'#FF5E04',
							'#F1780E',
							'#E9DE0A',
							'#FFDF1F',
							'#F3B605',
							'#1E52A4',
							'#066FAD',
							'#2AA9D3',
							'#D80122',
							'#EB001B',
							'#FF2A42',
							'#F3A2B2',
							'#813D88',
							'#610DF4',
							'#D2B8F8',
							'#18704B',
							'#8DBE09',
							'#21D863',
						]}
					/>
				</Group>
				{/* Input Group */}
				<Group
					grow
					mt="lg"
				>
					{/* Active */}
					<Checkbox
						label="Activo"
						checked={form.values.active}
						onChange={(event) => form.setFieldValue('active', event.currentTarget.value)}
						{...form.getInputProps('active')}
					/>
				</Group>
				<Divider
					my="md"
				/>
				{/* Files */}
				<Group
					noWrap
					mt="lg"
					align="flex-start"
				>
					{/* Logo */}
					<Box>
						{/* Subtitle */}
						<Title
							order={5}
							mb="md"
						>
							Logotipo
						</Title>
						{/* Dropzone */}
						<Group>
							{/* Zone */}
							<TenantLogoDropzone
								text="Sube el logotipo de este cliente"
								onDrop={onDropLogo}
								onReject={onReject}
							/>
							{/* Error */}
							{form.errors?.logo && (
								<Text className={classes.fieldError}>
									{form.errors?.logo.toString()}
								</Text>
							)}
							{/* Preview */}
							{form.values.logo !== null ?
								<Box className={classes.imageContainer}>
									<Image
										withPlaceholder
										src={isEdit && !isLogoChanged ? tenant.logo : fileURL}
										imageProps={isEdit && !isLogoChanged ? null : { onLoad: () => URL.revokeObjectURL(fileURL) }}
										height={148}
										width={148}
										radius="sm"
									/>
								</Box>
							: null}
						</Group>
					</Box>
					{/* Isotype */}
					<Box>
						{/* Subtitle */}
						<Title
							order={5}
							mb="md"
						>
							Isotipo
						</Title>
						{/* Dropzone */}
						<Group>
							{/* Zone */}
							<TenantLogoDropzone
								text="Sube el isotipo de este cliente"
								onDrop={onDropIsotype}
								onReject={onReject}
							/>
							{/* Error */}
							{form.errors?.isotype && (
								<Text className={classes.fieldError}>
									{form.errors?.isotype.toString()}
								</Text>
							)}
							{/* Preview */}
							{form.values.isotype !== null ?
								<Box className={classes.imageContainer}>
									<Image
										withPlaceholder
										src={isEdit && !isIsotypeChanged ? tenant.isotype : isotypeURL}
										imageProps={isEdit && !isIsotypeChanged ? null : { onLoad: () => URL.revokeObjectURL(isotypeURL) }}
										height={148}
										width={148}
										radius="sm"
									/>
								</Box>
							: null}
						</Group>
					</Box>
				</Group>
				{/* Buttons */}
				<Group
					position="apart"
					grow={!isEdit}
				>
					{/* Cancel */}
					<Button
						mt="xl"
						color="red"
						onClick={closeModal}
						disabled={isLoading}
						variant="light"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						Cancelar
					</Button>
					{/* Submit */}
					<Button
						mt="xl"
						type="submit"
						loading={isLoading}
						variant="filled"
						sx={{ flexGrow: isEdit ? 2 : 1 }}
					>
						{isEdit ? 'Guardar Cambios' : 'Crear Cliente Corporativo'}
					</Button>
				</Group>
			</form>
		</>
	);

}

export default TenantForm;
